import React, {
  useMemo,
  useRef,
  useCallback,
  useState,
  useEffect,
  useLayoutEffect,
} from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
  HStack,
  VStack,
  Tooltip,
  Textarea,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react';
import { VariableSizeList as List } from 'react-window';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ListFilter,
  MinusCircle,
  ScrollText,
  X,
  XCircle,
  CircleHelp,
  Pencil,
} from 'lucide-react';
import { ROW_HEIGHT } from '../../../utils/tableUtils';
import ComplianceStatusChip from './ComplianceStatusChip';
import ComplianceDecisionPopover from './ComplianceDecisionPopover';
import ComplianceContentDiff from '../compliance/ComplianceContentDiff';
import { useReviewerDecisionMutation } from '../../../hooks/useReviewerDecisionMutation';
import { useTemplateComplianceResolveMutation } from '../../../hooks/useTemplateComplianceResolve';
import { useAuth } from '../../../contexts/AuthContext';
import { COMPLIANCE_FILTERS } from '../../../utils/complianceFilters';

/**
 * ReviewerCampaignTable
 *
 * Read-only (Phase 3.1/3.2) reviewer surface: one row per contact, one
 * column group per message draft.
 *
 * Architecture mirrors the main virtualized persona table:
 *   - Virtualized rows via react-window
 *   - First column (Name) is frozen in a left rail that never scrolls
 *     horizontally — it scrolls vertically in sync with the right rail
 *     via a shared scroll offset.
   *   - Right rail holds everything else: Title, Location, and per-draft
   *     {Subject (optional if no row has subject text), Body, Status, Note}.
   *     Prompt/template controls attach to the Body column header.
 *
 * Click-to-view modal for any truncated content (subject / body / note).
 * The Status chip is the *only* editable surface — clicking it opens
 * `ComplianceDecisionPopover`, which is the single place a reviewer
 * authors a verdict + note. The Note column displays the same `note`
 * string as a read-only mirror so reviewers can scan their own context
 * without re-opening every popover. Single source of truth, no double
 * editing path.
 *
 * Edit eligibility (Phase 3.3):
 *   - Cell has no decision yet → editable by anyone with access.
 *   - Cell decided by current user (`decided_by='human' AND
 *     reviewer_user_id === currentUserId`) → editable.
 *   - Cell decided by AI or another reviewer → read-only chip, no
 *     popover. Co-reviewers can disagree by writing their own decision
 *     once the multi-reviewer model lands; until then we don't let one
 *     reviewer overwrite another.
 */

const HEADER_HEIGHT = 42;

const FROZEN_RAIL_BG = {
  bg: '#0a0a0a',
  borderRight: '1px solid',
  borderColor: 'rgba(255,255,255,0.08)',
};

const COLUMN_WIDTHS = {
  name: 260,
  title: 180,
  location: 180,
  subject: 260,
  body: 320,
  status: 160,
  note: 260,
};

/**
 * Header labels follow the advisor-side convention: single row with
 * `<Draft Name>: <Column>` per column. Keeps the reviewer UI visually
 * consistent with what the advisor already sees in their table and
 * avoids a two-row grouping band that reads as clutter when the draft
 * count is small.
 */
const formatColumnHeader = (col) =>
  col.draftLabel ? `${col.draftLabel}: ${col.label}` : col.label;

/** Any contact row with non-empty subject for this draft → show Subject column. */
const draftGroupHasSubjectLine = (contacts, configId) => {
  if (!Array.isArray(contacts) || contacts.length === 0) return true;
  for (const row of contacts) {
    const s = row.drafts?.[configId]?.subject;
    if (s != null && String(s).trim().length > 0) return true;
  }
  return false;
};

const STATUS_FILTER_OPTIONS = [
  { key: COMPLIANCE_FILTERS.COMPLIANT, label: 'Compliant', icon: Check, color: '#34D399' },
  { key: COMPLIANCE_FILTERS.NON_COMPLIANT, label: 'Non-compliant', icon: X, color: '#EF4444' },
  { key: COMPLIANCE_FILTERS.NEEDS_HUMAN_REVIEW, label: 'Needs human', icon: CircleHelp, color: '#A78BFA' },
  { key: COMPLIANCE_FILTERS.STALE, label: 'Changed since review', icon: AlertTriangle, color: '#FBBF24' },
  { key: COMPLIANCE_FILTERS.NOT_REVIEWED, label: 'Not reviewed', icon: MinusCircle, color: 'rgba(255,255,255,0.72)' },
];

const buildRightColumns = (drafts, contactsForSubjectVisibility = []) => {
  const cols = [
    { id: 'title', label: 'Title', width: COLUMN_WIDTHS.title, kind: 'title' },
    {
      id: 'location',
      label: 'Location',
      width: COLUMN_WIDTHS.location,
      kind: 'location',
    },
  ];
  drafts.forEach((draft, idx) => {
    const configId = draft.config_id;
    if (draftGroupHasSubjectLine(contactsForSubjectVisibility, configId)) {
      cols.push({
        id: `draft-${draft.config_id}-subject`,
        label: 'Subject',
        draftLabel: draft.display_name,
        draftIndex: idx,
        width: COLUMN_WIDTHS.subject,
        kind: 'subject',
        configId: draft.config_id,
      });
    }
    cols.push({
      id: `draft-${draft.config_id}-body`,
      label: 'Body',
      draftLabel: draft.display_name,
      draftIndex: idx,
      width: COLUMN_WIDTHS.body,
      kind: 'body',
      configId: draft.config_id,
      source: draft.source || null,
      templateReview: draft.template_review || null,
    });
    cols.push({
      id: `draft-${draft.config_id}-status`,
      label: 'Status',
      draftLabel: draft.display_name,
      draftIndex: idx,
      width: COLUMN_WIDTHS.status,
      kind: 'status',
      configId: draft.config_id,
    });
    cols.push({
      id: `draft-${draft.config_id}-note`,
      label: 'Note',
      draftLabel: draft.display_name,
      draftIndex: idx,
      width: COLUMN_WIDTHS.note,
      kind: 'note',
      configId: draft.config_id,
    });
  });
  return cols;
};

// ─────────────────────────────────────────────────────────────────────────────
// Modal for click-to-view text (read-only in Phase 3.2)
// ─────────────────────────────────────────────────────────────────────────────

const ReviewerCellModal = ({ isOpen, onClose, title, subtitle, value }) => (
  <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered>
    <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(3px)" />
    <ModalContent
      bg="#0f0f0f"
      border="1px solid rgba(255,255,255,0.10)"
      color="#fafafa"
      borderRadius="12px"
    >
      <ModalHeader pb={2}>
        <VStack align="start" spacing={0.5}>
          {subtitle ? (
            <Text
              fontSize="11px"
              color="rgba(255,255,255,0.45)"
              letterSpacing="0.06em"
              textTransform="uppercase"
            >
              {subtitle}
            </Text>
          ) : null}
          <Text fontSize="md" fontWeight="600" color="#fafafa">
            {title}
          </Text>
        </VStack>
      </ModalHeader>
      <ModalCloseButton color="rgba(255,255,255,0.6)" />
      <ModalBody pb={6}>
        <Box
          whiteSpace="pre-wrap"
          fontSize="sm"
          color="rgba(255,255,255,0.85)"
          lineHeight="1.65"
          maxH="60vh"
          overflowY="auto"
          border="1px solid rgba(255,255,255,0.06)"
          borderRadius="8px"
          bg="rgba(255,255,255,0.02)"
          p={4}
        >
          {value || (
            <Text as="span" color="rgba(255,255,255,0.35)" fontStyle="italic">
              Empty
            </Text>
          )}
        </Box>
      </ModalBody>
    </ModalContent>
  </Modal>
);

const ReviewerCellDiffModal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  beforeValue,
  currentValue,
}) => (
  <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered>
    <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(3px)" />
    <ModalContent
      bg="#0f0f0f"
      border="1px solid rgba(255,255,255,0.10)"
      color="#fafafa"
      borderRadius="12px"
    >
      <ModalHeader pb={2}>
        <VStack align="start" spacing={1}>
          {subtitle ? (
            <Text
              fontSize="11px"
              color="rgba(255,255,255,0.45)"
              letterSpacing="0.06em"
              textTransform="uppercase"
            >
              {subtitle}
            </Text>
          ) : null}
          <HStack spacing={2} align="center">
            <Text fontSize="md" fontWeight="600" color="#fafafa">
              {title}
            </Text>
            <Badge
              bg="rgba(251,191,36,0.10)"
              color="#FBBF24"
              border="1px solid"
              borderColor="rgba(251,191,36,0.35)"
              fontSize="10px"
              textTransform="none"
              fontWeight="500"
              px={1.5}
              borderRadius="full"
            >
              Content changed since review
            </Badge>
          </HStack>
        </VStack>
      </ModalHeader>
      <ModalCloseButton color="rgba(255,255,255,0.6)" />
      <ModalBody pb={6}>
        <ComplianceContentDiff
          title="What you reviewed vs. now"
          beforeText={beforeValue}
          afterText={currentValue}
        />
      </ModalBody>
    </ModalContent>
  </Modal>
);

// ─────────────────────────────────────────────────────────────────────────────
// Draft source modal — shows the advisor-authored template/prompt per draft
// ─────────────────────────────────────────────────────────────────────────────

const MODAL_CONTENT_STYLE = {
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: '8px',
  bg: 'rgba(255,255,255,0.02)',
  p: 4,
  overflowY: 'auto',
};

/** Split a template string on {{variable}} tokens and render them distinctly. */
const TemplateText = ({ text }) => {
  if (!text) return <Text as="span" color="rgba(255,255,255,0.35)" fontStyle="italic">—</Text>;
  const parts = text.split(/(\{\{[^}]+\}\})/g);
  return (
    <Text fontSize="sm" color="rgba(255,255,255,0.82)" whiteSpace="pre-wrap" lineHeight="1.7">
      {parts.map((part, i) =>
        /^\{\{[^}]+\}\}$/.test(part) ? (
          <Box
            key={i}
            as="span"
            bg="rgba(255,255,255,0.1)"
            border="1px solid rgba(255,255,255,0.18)"
            borderRadius="4px"
            px="5px"
            py="1px"
            fontFamily="mono"
            fontSize="xs"
            color="rgba(255,255,255,0.75)"
          >
            {part}
          </Box>
        ) : (
          part
        )
      )}
    </Text>
  );
};

const SourceSection = ({ label, children }) => (
  <VStack align="stretch" spacing={2}>
    <Text
      fontSize="10px"
      fontWeight="700"
      color="rgba(255,255,255,0.4)"
      textTransform="uppercase"
      letterSpacing="0.07em"
    >
      {label}
    </Text>
    <Box {...MODAL_CONTENT_STYLE} maxH="200px">
      {children}
    </Box>
  </VStack>
);

const MODE_LABELS = {
  pure_template: 'Template',
  template: 'Template + AI personalization',
  scratch: 'AI-generated',
};

/** Min note length when marking template non-compliant at the gate modal. */
const TEMPLATE_GATE_NOTE_MIN_NON_COMPLIANT = 3;

/**
 * FastAPI may return `detail` as a string, `{ message }`, or a Pydantic error list.
 */
function formatTemplateResolveErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === 'string') return detail;
  if (
    typeof detail === 'object' &&
    !Array.isArray(detail) &&
    typeof detail.message === 'string'
  ) {
    return detail.message;
  }
  if (Array.isArray(detail)) {
    const parts = detail
      .map((e) => {
        if (typeof e === 'string') return e;
        if (!e || typeof e !== 'object') return '';
        const loc = Array.isArray(e.loc)
          ? e.loc.filter((x) => x !== 'body').join('.')
          : '';
        const msg = e.msg || '';
        return loc ? `${loc}: ${msg}` : msg;
      })
      .filter(Boolean);
    return parts.length ? parts.join('; ') : null;
  }
  return null;
}

/** Resolved Phase 1 template verdict (`verdict` field or legacy `compliant` bool). */
function normalizeTemplatePhase1Verdict(templateReview) {
  if (!templateReview) return null;
  const v = templateReview.verdict;
  if (v === 'compliant' || v === 'non_compliant' || v === 'needs_human_review')
    return v;
  const c = templateReview.compliant;
  if (c === true) return 'compliant';
  if (c === false) return 'non_compliant';
  return null;
}

const TEMPLATE_PHASE1_VERDICT_STYLES = {
  compliant: {
    label: 'Compliant',
    color: '#34D399',
    bgColor: 'rgba(52,211,153,0.08)',
    borderColor: 'rgba(52,211,153,0.25)',
    Icon: CheckCircle2,
  },
  non_compliant: {
    label: 'Non-compliant',
    color: '#EF4444',
    bgColor: 'rgba(239,68,68,0.08)',
    borderColor: 'rgba(239,68,68,0.25)',
    Icon: XCircle,
  },
  needs_human_review: {
    label: 'Needs human',
    color: '#A78BFA',
    bgColor: 'rgba(167,139,250,0.08)',
    borderColor: 'rgba(167,139,250,0.25)',
    Icon: CircleHelp,
  },
};

/** Matches ``ComplianceStatusChip`` STALE_STYLE — source prompt/template staleness. */
const TEMPLATE_SOURCE_STALE_STYLE = {
  color: '#FBBF24',
  bgColor: 'rgba(251,191,36,0.10)',
  borderColor: 'rgba(251,191,36,0.30)',
};

/**
 * Inline compliance verdict banner rendered at the top of the source modal.
 * Shows a green check (compliant), red X (non-compliant), or purple help (needs human)
 * with the AI's reasoning below it.
 */
const TemplateComplianceBanner = ({ templateReview, onEdit }) => {
  const verdict = normalizeTemplatePhase1Verdict(templateReview);
  if (!templateReview || !verdict) {
    return null;
  }

  const isStale = Boolean(templateReview.is_stale);
  const verdictStyle = TEMPLATE_PHASE1_VERDICT_STYLES[verdict];
  const { label } = verdictStyle;
  const Icon = isStale ? AlertTriangle : verdictStyle.Icon;
  const color = isStale ? TEMPLATE_SOURCE_STALE_STYLE.color : verdictStyle.color;
  const bgColor = isStale ? TEMPLATE_SOURCE_STALE_STYLE.bgColor : verdictStyle.bgColor;
  const borderColor = isStale ? TEMPLATE_SOURCE_STALE_STYLE.borderColor : verdictStyle.borderColor;
  const staleTooltip = isStale
    ? `${label} — source changed since review. Re-check before sending.`
    : null;

  const bannerInner = (
    <Box
      border="1px solid"
      borderColor={borderColor}
      borderRadius="8px"
      bg={bgColor}
      p={3}
    >
      <HStack spacing={2} mb={templateReview.reasoning ? 2 : 0} flexWrap="wrap">
        <Box color={color} flexShrink={0}>
          <Icon size={16} strokeWidth={isStale ? 2.25 : 2} />
        </Box>
        <Text fontSize="sm" fontWeight="600" color={color}>
          {label}
        </Text>
        <HStack ml="auto" spacing={2} flexShrink={0} align="center">
          {typeof onEdit === 'function' ? (
            <Button
              size="xs"
              variant="ghost"
              color="rgba(255,255,255,0.65)"
              _hover={{ bg: 'rgba(255,255,255,0.06)', color: '#fafafa' }}
              leftIcon={<Pencil size={12} strokeWidth={2} />}
              onClick={onEdit}
            >
              Edit
            </Button>
          ) : null}
          <Text fontSize="xs" color="rgba(255,255,255,0.35)">
            {templateReview.reviewed_by === 'human' ? 'Reviewer' : 'AI review'}
          </Text>
        </HStack>
      </HStack>
      {templateReview.reasoning && (
        <Text
          fontSize="xs"
          color="rgba(255,255,255,0.72)"
          lineHeight="1.65"
          whiteSpace="pre-wrap"
          mt={1}
        >
          {templateReview.reasoning}
        </Text>
      )}
    </Box>
  );

  if (!staleTooltip) return bannerInner;
  return (
    <Tooltip
      label={staleTooltip}
      placement="top"
      openDelay={280}
      bg="rgba(20,20,30,0.95)"
      color="whiteAlpha.800"
      fontSize="xs"
      borderRadius="6px"
      px={2}
      py={1}
      maxW="280px"
      hasArrow
    >
      {bannerInner}
    </Tooltip>
  );
};

/**
 * Line diff of the advisor-authored source at template-review time vs the live
 * template/prompt shown in this modal (mirrors per-cell ComplianceContentDiff).
 */
const TemplatePromptSourceDiff = ({ source, templateReview }) => {
  if (!source || !templateReview?.is_stale) return null;

  const rs = templateReview.reviewed_subject_template;
  const rb = templateReview.reviewed_body_template;
  const ru = templateReview.reviewed_user_prompt;
  const hasSnap =
    (typeof rs === 'string' && rs.length > 0) ||
    (typeof rb === 'string' && rb.length > 0) ||
    (typeof ru === 'string' && ru.length > 0);

  if (!hasSnap) {
    return null;
  }

  if (source.mode === 'scratch') {
    return (
      <Box>
        <HStack spacing={1.5} mb={2} align="center">
          <Box color="#FBBF24" display="inline-flex" flexShrink={0}>
            <AlertTriangle size={14} strokeWidth={2.25} aria-hidden />
          </Box>
          <Text
            fontSize="11px"
            fontWeight="600"
            color="rgba(251,191,36,0.95)"
            letterSpacing="0.04em"
            textTransform="uppercase"
          >
            Prompt changed since template review
          </Text>
        </HStack>
        <ComplianceContentDiff
          title="AI prompt — when reviewed vs now"
          beforeText={ru || ''}
          afterText={source.user_prompt || ''}
        />
      </Box>
    );
  }

  return (
    <Box>
      <HStack spacing={1.5} mb={2} align="center">
        <Box color="#FBBF24" display="inline-flex" flexShrink={0}>
          <AlertTriangle size={14} strokeWidth={2.25} aria-hidden />
        </Box>
        <Text
          fontSize="11px"
          fontWeight="600"
          color="rgba(251,191,36,0.95)"
          letterSpacing="0.04em"
          textTransform="uppercase"
        >
          Template changed since review
        </Text>
      </HStack>
      <VStack align="stretch" spacing={3}>
        {((typeof rs === 'string' && rs.length > 0) || (source.subject_template || '').length > 0) && (
          <ComplianceContentDiff
            title="Subject template — when reviewed vs now"
            beforeText={typeof rs === 'string' ? rs : ''}
            afterText={source.subject_template || ''}
          />
        )}
        <ComplianceContentDiff
          title="Body template — when reviewed vs now"
          beforeText={typeof rb === 'string' ? rb : ''}
          afterText={source.body_template || ''}
        />
      </VStack>
      <Text fontSize="10px" color="rgba(255,255,255,0.42)" mt={2} lineHeight="1.5">
        Staleness can also come from personalization-variable instructions not shown here.
      </Text>
    </Box>
  );
};

/** Note + actions to set template-level compliant / non-compliant. */
const TemplateVerdictEditorForm = ({
  resolveNote,
  onResolveNoteChange,
  onResolveCompliant,
  onResolveNonCompliant,
  onCancel,
  showCancel,
  loadingCompliant,
  loadingNonCompliant,
}) => (
  <Box
    border="1px solid rgba(255,255,255,0.08)"
    borderRadius="10px"
    bg="rgba(255,255,255,0.02)"
    p={4}
  >
    <Text fontSize="11px" fontWeight="600" color="rgba(255,255,255,0.45)" mb={1.5}>
      Note{' '}
      <Text as="span" color="rgba(248,113,113,0.88)">
        (required if non-compliant — min {TEMPLATE_GATE_NOTE_MIN_NON_COMPLIANT} chars)
      </Text>
    </Text>
    <Textarea
      value={resolveNote}
      onChange={(e) => onResolveNoteChange(e.target.value)}
      placeholder="Brief rationale for advisors and audit (especially if non-compliant)…"
      bg="rgba(255,255,255,0.04)"
      border="1px solid rgba(255,255,255,0.10)"
      color="#fafafa"
      fontSize="sm"
      _placeholder={{ color: 'rgba(255,255,255,0.35)' }}
      rows={5}
      minH="112px"
      resize="vertical"
      mb={3}
      isDisabled={loadingCompliant || loadingNonCompliant}
    />
    <HStack spacing={3} justify="flex-end" flexWrap="wrap">
      {showCancel && typeof onCancel === 'function' ? (
        <Button
          size="sm"
          variant="ghost"
          color="rgba(255,255,255,0.65)"
          _hover={{ bg: 'rgba(255,255,255,0.06)' }}
          onClick={onCancel}
          isDisabled={loadingCompliant || loadingNonCompliant}
        >
          Cancel
        </Button>
      ) : null}
      <Button
        size="sm"
        variant="outline"
        borderColor="rgba(239,68,68,0.45)"
        color="#F87171"
        _hover={{ bg: 'rgba(239,68,68,0.12)' }}
        onClick={onResolveNonCompliant}
        isDisabled={loadingCompliant || loadingNonCompliant}
        isLoading={loadingNonCompliant}
        loadingText="Applying…"
        leftIcon={<XCircle size={16} strokeWidth={2} color="#EF4444" />}
      >
        Template non-compliant
      </Button>
      <Button
        size="sm"
        bg="#34D399"
        color="#0a0a0a"
        _hover={{ opacity: 0.9 }}
        onClick={onResolveCompliant}
        isDisabled={loadingCompliant || loadingNonCompliant}
        isLoading={loadingCompliant}
        loadingText="Starting…"
        leftIcon={<CheckCircle2 size={16} strokeWidth={2} color="#065f46" />}
      >
        Template compliant
      </Button>
    </HStack>
  </Box>
);

/**
 * Modal that renders the advisor-authored source of a message draft.
 * Switches on `source.mode` to present the right layout — consistent
 * with the three drafting paths in the advisor's UI.
 *
 * `templateReview` carries the AI Phase 1 verdict for the template/prompt
 * itself and is shown as a compliance banner at the top of the modal.
 */
const ReviewerDraftSourceModal = ({
  isOpen,
  onClose,
  draftLabel,
  source,
  templateReview,
  campaignId,
  configId,
  reviewerContactCount = 0,
  onObserveComplianceTail,
}) => {
  const toast = useToast();
  const resolveMutation = useTemplateComplianceResolveMutation(campaignId);
  const [gateNote, setGateNote] = useState('');
  const [editingVerdict, setEditingVerdict] = useState(false);
  const prevIsOpenRef = useRef(false);

  const phase1Verdict = normalizeTemplatePhase1Verdict(templateReview);
  const hasTemplateVerdict = phase1Verdict != null;

  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      const v = normalizeTemplatePhase1Verdict(templateReview);
      setEditingVerdict(v == null);
      setGateNote(typeof templateReview?.reasoning === 'string' ? templateReview.reasoning : '');
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, templateReview]);

  if (!source) return null;
  const modeLabel = MODE_LABELS[source.mode] ?? source.mode;
  /** When true, show template compliant / non-compliant actions (human gate or manual override). */
  const showGateResolve = Boolean(campaignId && configId);
  const templateVerdictIsGate =
    normalizeTemplatePhase1Verdict(templateReview) === 'needs_human_review';

  const handleCancelEdit = () => {
    setEditingVerdict(false);
    setGateNote(typeof templateReview?.reasoning === 'string' ? templateReview.reasoning : '');
  };

  const handleResolveCompliant = async () => {
    try {
      await resolveMutation.mutateAsync({
        configId,
        resolution: 'compliant',
        note: gateNote.trim() || null,
        overrideExistingVerdict: !templateVerdictIsGate,
      });
      toast({
        title: 'Template marked compliant',
        description:
          source.mode === 'pure_template'
            ? 'Every message in this column now matches the template verdict (static template).'
            : 'Per-contact AI compliance has started — watch the toolbar pill for progress; cells update as results arrive.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      void onObserveComplianceTail?.({
        configIds: [String(configId)],
        expectedCells:
          typeof reviewerContactCount === 'number' && reviewerContactCount >= 0
            ? reviewerContactCount
            : 0,
      });
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg =
        formatTemplateResolveErrorDetail(detail) ||
        e?.message ||
        'Could not apply resolution.';
      toast({
        title: 'Could not resolve',
        description: typeof msg === 'string' ? msg : 'Refresh and try again.',
        status: 'error',
        duration: 6500,
        isClosable: true,
      });
    }
  };

  const handleResolveNonCompliant = async () => {
    const n = gateNote.trim();
    if (n.length < TEMPLATE_GATE_NOTE_MIN_NON_COMPLIANT) {
      toast({
        title: 'Note too short',
        description: `Add at least ${TEMPLATE_GATE_NOTE_MIN_NON_COMPLIANT} characters explaining the issue.`,
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    try {
      await resolveMutation.mutateAsync({
        configId,
        resolution: 'non_compliant',
        note: n,
        overrideExistingVerdict: !templateVerdictIsGate,
      });
      toast({
        title: 'Template marked non-compliant',
        description: 'Messages in this column are being updated to match the template verdict.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      void onObserveComplianceTail?.({
        configIds: [String(configId)],
        expectedCells:
          typeof reviewerContactCount === 'number' && reviewerContactCount >= 0
            ? reviewerContactCount
            : 0,
      });
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg =
        formatTemplateResolveErrorDetail(detail) ||
        e?.message ||
        'Could not apply resolution.';
      toast({
        title: 'Could not resolve',
        description: typeof msg === 'string' ? msg : 'Refresh and try again.',
        status: 'error',
        duration: 6500,
        isClosable: true,
      });
    }
  };

  const busyResolve = resolveMutation.isPending;
  const loadingNc =
    busyResolve &&
    resolveMutation.variables?.resolution === 'non_compliant';
  const loadingOk =
    busyResolve && resolveMutation.variables?.resolution === 'compliant';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered>
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(3px)" />
      <ModalContent
        bg="#0f0f0f"
        border="1px solid rgba(255,255,255,0.10)"
        color="#fafafa"
        borderRadius="12px"
      >
        <ModalHeader pb={2}>
          <VStack align="start" spacing={0.5}>
            <Text
              fontSize="11px"
              color="rgba(255,255,255,0.45)"
              letterSpacing="0.06em"
              textTransform="uppercase"
            >
              {draftLabel} · {modeLabel}
            </Text>
            <Text fontSize="md" fontWeight="600" color="#fafafa">
              Original message source
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton color="rgba(255,255,255,0.6)" />
        <ModalBody pb={6}>
          <VStack align="stretch" spacing={5}>
            {showGateResolve ? (
              <>
                {hasTemplateVerdict && !editingVerdict ? (
                  <TemplateComplianceBanner
                    templateReview={templateReview}
                    onEdit={() => setEditingVerdict(true)}
                  />
                ) : null}
                <TemplatePromptSourceDiff source={source} templateReview={templateReview} />
                {editingVerdict || !hasTemplateVerdict ? (
                  <TemplateVerdictEditorForm
                    resolveNote={gateNote}
                    onResolveNoteChange={setGateNote}
                    onResolveCompliant={handleResolveCompliant}
                    onResolveNonCompliant={handleResolveNonCompliant}
                    onCancel={handleCancelEdit}
                    showCancel={hasTemplateVerdict && editingVerdict}
                    loadingCompliant={loadingOk}
                    loadingNonCompliant={loadingNc}
                  />
                ) : null}
                <Divider borderColor="rgba(255,255,255,0.08)" />
              </>
            ) : null}

            {/* Plain source view — hidden when the diff is already showing all content */}
            {!templateReview?.is_stale && (
              source.mode === 'scratch' ? (
                <>
                  <SourceSection label="AI prompt">
                    <Text
                      fontSize="sm"
                      color="rgba(255,255,255,0.82)"
                      whiteSpace="pre-wrap"
                      lineHeight="1.7"
                    >
                      {source.user_prompt || <Text as="span" color="rgba(255,255,255,0.35)" fontStyle="italic">—</Text>}
                    </Text>
                  </SourceSection>
                </>
              ) : (
                <>
                  {source.subject_template != null && (
                    <SourceSection label="Subject template">
                      <TemplateText text={source.subject_template} />
                    </SourceSection>
                  )}
                  {source.body_template != null && (
                    <SourceSection label="Body template">
                      <TemplateText text={source.body_template} />
                    </SourceSection>
                  )}
                </>
              )
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

/**
 * Self-contained info button that opens ReviewerDraftSourceModal.
 * Rendered in the **Body** column header — one info icon per draft,
 * alongside Phase 1 template verdict when present.
 */
const DraftSourceButton = ({
  campaignId,
  configId,
  draftLabel,
  source,
  templateReview,
  reviewerContactCount,
  onObserveComplianceTail,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const isTemplateStale = Boolean(templateReview?.is_stale);
  const verdict = normalizeTemplatePhase1Verdict(templateReview);
  const hasVerdict = verdict != null;
  const style = hasVerdict ? TEMPLATE_PHASE1_VERDICT_STYLES[verdict] : null;
  const showStatusGlyph = hasVerdict || isTemplateStale;
  const verdictColor = isTemplateStale
    ? TEMPLATE_SOURCE_STALE_STYLE.color
    : style?.color;
  const VerdictMiniIcon = isTemplateStale ? AlertTriangle : style?.Icon;
  let tooltipLabel = 'View original message template or prompt';
  if (isTemplateStale) {
    tooltipLabel = hasVerdict
      ? `Source changed since review (${style.label}) — click to view details`
      : 'Source changed since review — click to view details';
  } else if (hasVerdict) {
    tooltipLabel = `Template ${style.label.toLowerCase()} — click to view details`;
  }

  return (
    <>
      <Tooltip
        label={tooltipLabel}
        placement="top"
        openDelay={300}
        bg="rgba(20,20,30,0.95)"
        color="whiteAlpha.800"
        fontSize="xs"
        borderRadius="6px"
        px={2}
        py={1}
      >
        <Box
          as="button"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          gap="4px"
          flexShrink={0}
          ml={2.0}
          color="rgba(255,255,255,0.45)"
          _hover={{ color: 'rgba(255,255,255,0.9)' }}
          transition="color 0.15s"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(true);
          }}
          aria-label="View original message source"
        >
          <ScrollText size={15} strokeWidth={1.6} />
          {showStatusGlyph && verdictColor != null && VerdictMiniIcon && (
            <Box color={verdictColor} display="inline-flex" alignItems="center">
              <VerdictMiniIcon size={12} strokeWidth={2.25} />
            </Box>
          )}
        </Box>
      </Tooltip>
      <ReviewerDraftSourceModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        draftLabel={draftLabel}
        source={source}
        templateReview={templateReview}
        campaignId={campaignId}
        configId={configId}
        reviewerContactCount={reviewerContactCount}
        onObserveComplianceTail={onObserveComplianceTail}
      />
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Cells
// ─────────────────────────────────────────────────────────────────────────────

const ClickableTextCell = ({
  value,
  modalTitle,
  modalSubtitle,
  placeholder,
  isStale = false,
  reviewedValue,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasValue = value != null && String(value).trim().length > 0;
  const hasReviewedSnapshot = reviewedValue !== undefined && reviewedValue !== null;
  const showDiffOnOpen = isStale && hasReviewedSnapshot;
  const canOpen = hasValue || showDiffOnOpen;
  const tooltipLabel = isStale
    ? 'Content changed since review'
    : (hasValue ? 'Click to view' : null);

  return (
    <>
      <Tooltip
        label={tooltipLabel}
        placement="top"
        openDelay={400}
        bg="rgba(20,20,30,0.95)"
        color="whiteAlpha.800"
        fontSize="xs"
        borderRadius="6px"
        px={2}
        py={1}
        isDisabled={!tooltipLabel}
      >
        <Box
          w="100%"
          minW={0}
          position="relative"
          cursor={canOpen ? 'pointer' : 'default'}
          onClick={canOpen ? () => setIsOpen(true) : undefined}
          _hover={canOpen ? { opacity: 0.8 } : undefined}
          transition="opacity 0.1s"
        >
          {isStale ? (
            <Box
              position="absolute"
              bottom={0}
              right={0}
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              color="#FBBF24"
              pointerEvents="none"
              aria-hidden="true"
            >
              <AlertTriangle size={12} strokeWidth={2.25} />
            </Box>
          ) : null}
          {hasValue ? (
            <Text
              fontSize="xs"
              color="rgba(255,255,255,0.82)"
              noOfLines={2}
              lineHeight="1.4"
              wordBreak="break-word"
              pr={isStale ? 5 : 0}
              pb={isStale ? 4 : 0}
            >
              {value}
            </Text>
          ) : (
            <Text
              fontSize="xs"
              color="rgba(255,255,255,0.28)"
              pr={isStale ? 5 : 0}
              pb={isStale ? 4 : 0}
            >
              {placeholder || '—'}
            </Text>
          )}
        </Box>
      </Tooltip>
      {showDiffOnOpen ? (
        <ReviewerCellDiffModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          title={modalTitle}
          subtitle={modalSubtitle}
          beforeValue={reviewedValue}
          currentValue={value}
        />
      ) : null}
      {hasValue && !showDiffOnOpen ? (
        <ReviewerCellModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          title={modalTitle}
          subtitle={modalSubtitle}
          value={value}
        />
      ) : null}
    </>
  );
};

const NameCell = ({ name }) => (
  <Text
    fontSize="xs"
    fontWeight="600"
    color="#fafafa"
    noOfLines={2}
    lineHeight="1.35"
    wordBreak="break-word"
  >
    {name || 'Unknown contact'}
  </Text>
);

const StaticTextCell = ({ value, fontWeight = '500' }) =>
  value ? (
    <Text
      fontSize="xs"
      color="rgba(255,255,255,0.78)"
      fontWeight={fontWeight}
      noOfLines={2}
      lineHeight="1.4"
      wordBreak="break-word"
    >
      {value}
    </Text>
  ) : (
    <Text fontSize="xs" color="rgba(255,255,255,0.28)">
      —
    </Text>
  );

const isEditableForCurrentUser = (compliance, currentUserId) => {
  if (!currentUserId) return false;
  if (!compliance || !compliance.status) return true;
  // AI verdicts are always overridable per-cell — any reviewer on the
  // share can flip them via the popover. The shared-decision audit
  // trail (reviewer_user_id + reviewer_display_name) still records
  // who triggered the AI run, but ownership doesn't gate edits.
  if (compliance.decided_by === 'ai') return true;
  if (compliance.decided_by !== 'human') return false;
  return compliance.reviewer_user_id === currentUserId;
};

const StatusCell = ({
  col,
  row,
  draftVals,
  currentUserId,
  mutateDecision,
  isMutating,
  mutationVariables,
}) => {
  const compliance = draftVals.compliance;
  const editable = isEditableForCurrentUser(compliance, currentUserId);
  // ``compliance.is_stale`` is set by GET /api/reviews/campaigns/:id when the
  // live subject+body hash no longer matches ``reviewed_content_hash`` on the
  // stored decision (see ``_build_contact_rows`` in ``reviews_api.py``).
  const chip = (
    <ComplianceStatusChip compliance={compliance} stalePrimaryLabel="Changed" />
  );

  if (!editable || !mutateDecision) return chip;

  const isSavingThisCell = Boolean(
    isMutating &&
      mutationVariables &&
      mutationVariables.contactMembershipId === row.contact_membership_id &&
      mutationVariables.configId === col.configId,
  );

  return (
    <ComplianceDecisionPopover
      trigger={chip}
      initialStatus={compliance?.status}
      initialNote={compliance?.note}
      isSaving={isSavingThisCell}
      onSave={({ status: nextStatus, note }) =>
        mutateDecision({
          contactMembershipId: row.contact_membership_id,
          configId: col.configId,
          status: nextStatus,
          note,
          expectedContentHash: draftVals.current_content_hash,
        })
      }
    />
  );
};

const RightCell = ({
  col,
  row,
  currentUserId,
  mutateDecision,
  isMutating,
  mutationVariables,
}) => {
  switch (col.kind) {
    case 'title':
      return <StaticTextCell value={row.title} />;
    case 'location':
      return <StaticTextCell value={row.location} />;
    case 'subject': {
      const draftVals = row.drafts?.[col.configId] || {};
      return (
        <ClickableTextCell
          value={draftVals.subject}
          modalTitle="Email subject"
          modalSubtitle={`${col.draftLabel} · ${row.name}`}
          placeholder="No subject"
          isStale={Boolean(draftVals.compliance?.is_stale)}
          reviewedValue={draftVals.compliance?.reviewed_subject}
        />
      );
    }
    case 'body': {
      const draftVals = row.drafts?.[col.configId] || {};
      return (
        <ClickableTextCell
          value={draftVals.body}
          modalTitle="Email body"
          modalSubtitle={`${col.draftLabel} · ${row.name}`}
          placeholder="No body"
          isStale={Boolean(draftVals.compliance?.is_stale)}
          reviewedValue={draftVals.compliance?.reviewed_body}
        />
      );
    }
    case 'status': {
      const draftVals = row.drafts?.[col.configId] || {};
      return (
        <StatusCell
          col={col}
          row={row}
          draftVals={draftVals}
          currentUserId={currentUserId}
          mutateDecision={mutateDecision}
          isMutating={isMutating}
          mutationVariables={mutationVariables}
        />
      );
    }
    case 'note': {
      const draftVals = row.drafts?.[col.configId] || {};
      const note = draftVals.compliance?.note;
      return (
        <ClickableTextCell
          value={note}
          modalTitle="Reviewer note"
          modalSubtitle={`${col.draftLabel} · ${row.name}`}
          placeholder="No note"
        />
      );
    }
    default:
      return null;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Virtualized rows
// ─────────────────────────────────────────────────────────────────────────────

const FrozenRow = React.memo(({ index, style, data }) => {
  const { rows, width } = data;
  const row = rows[index];
  if (!row) return null;
  return (
    <Box
      style={style}
      borderBottom="1px solid"
      borderColor="rgba(255,255,255,0.06)"
      _hover={{ bg: 'rgba(255,255,255,0.02)' }}
      transition="background 0.1s"
    >
      <Box
        w={`${width}px`}
        minW={`${width}px`}
        maxW={`${width}px`}
        h="100%"
        px={3}
        display="flex"
        alignItems="center"
        overflow="hidden"
      >
        <NameCell name={row.name} />
      </Box>
    </Box>
  );
});
FrozenRow.displayName = 'FrozenRow';

const ScrollRow = React.memo(({ index, style, data }) => {
  const {
    rows,
    columns,
    currentUserId,
    mutateDecision,
    isMutating,
    mutationVariables,
  } = data;
  const row = rows[index];
  if (!row) return null;
  return (
    <Box
      style={style}
      borderBottom="1px solid"
      borderColor="rgba(255,255,255,0.06)"
      _hover={{ bg: 'rgba(255,255,255,0.02)' }}
      transition="background 0.1s"
    >
      <HStack spacing={0} h="100%" align="stretch" w="100%">
        {columns.map((col) => (
          <Box
            key={col.id}
            w={`${col.width}px`}
            minW={`${col.width}px`}
            maxW={`${col.width}px`}
            px={3}
            display="flex"
            alignItems="center"
            borderRight="1px solid"
            borderColor="rgba(255,255,255,0.06)"
            overflow="hidden"
          >
            <RightCell
              col={col}
              row={row}
              currentUserId={currentUserId}
              mutateDecision={mutateDecision}
              isMutating={isMutating}
              mutationVariables={mutationVariables}
            />
          </Box>
        ))}
      </HStack>
    </Box>
  );
});
ScrollRow.displayName = 'ScrollRow';

// ─────────────────────────────────────────────────────────────────────────────
// Header
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Single-row header matching the advisor-side convention:
 *   Title | Location | (optional Subject) | Body (+ template/source) | ...
 *
 * Keeps the reviewer chrome in lockstep with what advisors already see
 * and avoids a two-row grouping band that reads as noise when there's
 * only one or two drafts.
 */
const RightHeader = ({
  columns,
  restTotalWidth,
  statusColumnFilters,
  onStatusColumnFilterChange,
  campaignId,
  reviewerContactCount,
  onObserveComplianceTail,
}) => (
  <HStack
    spacing={0}
    align="stretch"
    minH={`${HEADER_HEIGHT}px`}
    maxH={`${HEADER_HEIGHT}px`}
    minW={`${restTotalWidth}px`}
  >
    {columns.map((col) => {
      const label = formatColumnHeader(col);
      const showSourceIcon = col.kind === 'body' && col.source != null;
      const activeStatusFilter =
        col.kind === 'status' && statusColumnFilters
          ? statusColumnFilters[col.configId]
          : null;
      const activeFilterDef = activeStatusFilter
        ? STATUS_FILTER_OPTIONS.find((opt) => opt.key === activeStatusFilter) || null
        : null;
      return (
        <Box
          key={col.id}
          w={`${col.width}px`}
          minW={`${col.width}px`}
          maxW={`${col.width}px`}
          px={3}
          display="flex"
          alignItems="center"
          borderRight="1px solid"
          borderColor="rgba(255,255,255,0.08)"
        >
          <Tooltip
            label={label}
            placement="top"
            openDelay={400}
            bg="rgba(20,20,30,0.95)"
            color="whiteAlpha.800"
            fontSize="xs"
            borderRadius="6px"
            px={2}
            py={1}
            isDisabled={label.length < 32}
          >
            <Text
              fontWeight="600"
              color="rgba(255,255,255,0.5)"
              fontSize="11px"
              textTransform="uppercase"
              letterSpacing="0.05em"
              noOfLines={1}
              flex={1}
              minW={0}
            >
              {label}
            </Text>
          </Tooltip>

          {col.kind === 'status' && onStatusColumnFilterChange ? (
            <Menu placement="bottom-end" isLazy>
              <Tooltip
                label={
                  activeFilterDef
                    ? `Filtered: ${activeFilterDef.label}`
                    : 'Filter this status column'
                }
                placement="top"
                openDelay={300}
                bg="rgba(20,20,30,0.95)"
                color="whiteAlpha.800"
                fontSize="xs"
                borderRadius="6px"
                px={2}
                py={1}
              >
                <MenuButton
                  as={IconButton}
                  aria-label="Filter status column"
                  icon={<ListFilter size={14} strokeWidth={2} />}
                  size="xs"
                  variant="ghost"
                  ml={2}
                  minW="26px"
                  h="26px"
                  borderRadius="6px"
                  color={activeFilterDef ? activeFilterDef.color : 'rgba(255,255,255,0.38)'}
                  _hover={{
                    bg: 'rgba(255,255,255,0.06)',
                    color: activeFilterDef ? activeFilterDef.color : 'rgba(255,255,255,0.75)',
                  }}
                  _active={{ bg: 'rgba(255,255,255,0.10)' }}
                  _focus={{ boxShadow: 'none' }}
                  onClick={(e) => e.stopPropagation()}
                />
              </Tooltip>
              <MenuList
                bg="#0f0f0f"
                border="1px solid rgba(255,255,255,0.12)"
                borderRadius="10px"
                boxShadow="0 12px 40px rgba(0,0,0,0.55)"
                p={1}
                minW="220px"
              >
                {STATUS_FILTER_OPTIONS.map(({ key, label: optLabel, icon: Icon, color }) => (
                  <MenuItem
                    key={key}
                    icon={<Icon size={14} strokeWidth={2.25} color={color} />}
                    bg="transparent"
                    borderRadius="8px"
                    fontSize="sm"
                    color={activeStatusFilter === key ? '#fafafa' : 'rgba(255,255,255,0.78)'}
                    fontWeight={activeStatusFilter === key ? '600' : '500'}
                    _hover={{ bg: 'rgba(255,255,255,0.06)' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onStatusColumnFilterChange(col.configId, key);
                    }}
                  >
                    {optLabel}
                  </MenuItem>
                ))}
                <MenuDivider borderColor="rgba(255,255,255,0.10)" my={1} />
                <MenuItem
                  bg="transparent"
                  borderRadius="8px"
                  fontSize="sm"
                  color="rgba(255,255,255,0.65)"
                  _hover={{ bg: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.85)' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onStatusColumnFilterChange(col.configId, COMPLIANCE_FILTERS.ALL);
                  }}
                >
                  Clear filter
                </MenuItem>
              </MenuList>
            </Menu>
          ) : null}
          {showSourceIcon && (
            <DraftSourceButton
              campaignId={campaignId}
              configId={col.configId}
              draftLabel={col.draftLabel}
              source={col.source}
              templateReview={col.templateReview}
              reviewerContactCount={reviewerContactCount}
              onObserveComplianceTail={onObserveComplianceTail}
            />
          )}
        </Box>
      );
    })}
  </HStack>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_ROWS = [];

const ReviewerCampaignTable = ({
  campaignId,
  drafts = [],
  contacts = [],
  reviewerContactCount = 0,
  onObserveComplianceTail,
  contactRowsForColumnLayout = [],
  statusColumnFilters,
  onStatusColumnFilterChange,
  emptyMessage = 'No contacts to review on this campaign yet.',
}) => {
  const listBodyRef = useRef(null);
  const leftListRef = useRef(null);
  const rightListRef = useRef(null);
  const headerScrollRef = useRef(null);
  const bodyScrollRef = useRef(null);
  const syncingVerticalRef = useRef(false);

  const [listBodyHeight, setListBodyHeight] = useState(0);

  const { user } = useAuth();
  const currentUserId = user?.id || null;
  const decisionMutation = useReviewerDecisionMutation(campaignId);
  // We pass `mutateAsync` (not the whole mutation object) so memoized
  // children don't bust their refs every render.
  const { mutateAsync, isPending: isMutating, variables: mutationVariables } =
    decisionMutation;

  useEffect(() => {
    if (!listBodyRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setListBodyHeight(entry.contentRect.height);
      }
    });
    observer.observe(listBodyRef.current);
    return () => observer.disconnect();
  }, []);

  const rightColumns = useMemo(
    () => buildRightColumns(drafts, contactRowsForColumnLayout),
    [drafts, contactRowsForColumnLayout],
  );

  const restTotalWidth = useMemo(
    () => rightColumns.reduce((acc, col) => acc + col.width, 0),
    [rightColumns],
  );

  const rows = contacts && contacts.length ? contacts : EMPTY_ROWS;

  const frozenItemData = useMemo(
    () => ({ rows, width: COLUMN_WIDTHS.name }),
    [rows],
  );

  const scrollItemData = useMemo(
    () => ({
      rows,
      columns: rightColumns,
      currentUserId,
      mutateDecision: mutateAsync,
      isMutating,
      mutationVariables,
    }),
    [rows, rightColumns, currentUserId, mutateAsync, isMutating, mutationVariables],
  );

  const getItemSize = useCallback(() => ROW_HEIGHT, []);

  const onRightListScroll = useCallback(({ scrollOffset }) => {
    if (syncingVerticalRef.current) return;
    syncingVerticalRef.current = true;
    try {
      leftListRef.current?.scrollTo(scrollOffset);
    } finally {
      syncingVerticalRef.current = false;
    }
  }, []);

  const onLeftListScroll = useCallback(({ scrollOffset }) => {
    if (syncingVerticalRef.current) return;
    syncingVerticalRef.current = true;
    try {
      rightListRef.current?.scrollTo(scrollOffset);
    } finally {
      syncingVerticalRef.current = false;
    }
  }, []);

  useLayoutEffect(() => {
    const body = bodyScrollRef.current;
    const head = headerScrollRef.current;
    if (body && head) head.scrollLeft = body.scrollLeft;
  }, [restTotalWidth, rows.length]);

  const listHeight = Math.max(listBodyHeight, 120);

  if (drafts.length === 0) {
    return (
      <Box
        h="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={8}
        color="rgba(255,255,255,0.55)"
      >
        <VStack spacing={2} maxW="sm" textAlign="center">
          <Text fontSize="sm" fontWeight="600" color="rgba(255,255,255,0.75)">
            No outreach drafts yet
          </Text>
          <Text fontSize="xs" lineHeight="1.6">
            The advisor hasn't drafted any outreach messages on this campaign.
            This page will update as soon as drafts arrive — no refresh needed.
          </Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box
      h="100%"
      display="flex"
      flexDirection="column"
      overflow="hidden"
      position="relative"
    >
      {/* Header */}
      <HStack
        flexShrink={0}
        spacing={0}
        align="stretch"
        minH={`${HEADER_HEIGHT}px`}
        maxH={`${HEADER_HEIGHT}px`}
        bg="#0a0a0a"
        border="1px solid"
        borderColor="rgba(255,255,255,0.08)"
        zIndex={10}
      >
        <Box
          {...FROZEN_RAIL_BG}
          w={`${COLUMN_WIDTHS.name}px`}
          minW={`${COLUMN_WIDTHS.name}px`}
          maxW={`${COLUMN_WIDTHS.name}px`}
          px={3}
          display="flex"
          alignItems="center"
        >
          <Text
            fontWeight="600"
            color="rgba(255,255,255,0.5)"
            fontSize="11px"
            textTransform="uppercase"
            letterSpacing="0.05em"
            noOfLines={1}
          >
            Name
          </Text>
        </Box>
        <Box
          ref={headerScrollRef}
          flex={1}
          minW={0}
          overflowX="hidden"
          overflowY="hidden"
        >
          <RightHeader
            columns={rightColumns}
            restTotalWidth={restTotalWidth}
            statusColumnFilters={statusColumnFilters}
            onStatusColumnFilterChange={onStatusColumnFilterChange}
            campaignId={campaignId}
            reviewerContactCount={reviewerContactCount}
            onObserveComplianceTail={onObserveComplianceTail}
          />
        </Box>
      </HStack>

      {/* Body */}
      <Box ref={listBodyRef} flex={1} minH={0} overflow="hidden" display="flex" flexDirection="column">
      {rows.length === 0 ? (
        <HStack flex={1} minH={0} spacing={0} align="stretch">
          <Box
            {...FROZEN_RAIL_BG}
            w={`${COLUMN_WIDTHS.name}px`}
            minW={`${COLUMN_WIDTHS.name}px`}
            flexShrink={0}
          />
          <Box
            ref={bodyScrollRef}
            flex={1}
            minW={0}
            overflowX="auto"
            overflowY="hidden"
            onScroll={(e) => {
              const left = e.currentTarget.scrollLeft;
              if (
                headerScrollRef.current &&
                headerScrollRef.current.scrollLeft !== left
              ) {
                headerScrollRef.current.scrollLeft = left;
              }
            }}
          >
            <Box
              minW={`${restTotalWidth}px`}
              h={`${listHeight}px`}
              display="flex"
              alignItems="center"
              justifyContent="center"
              p={8}
              color="rgba(255,255,255,0.55)"
            >
              <Text fontSize="sm">{emptyMessage}</Text>
            </Box>
          </Box>
        </HStack>
      ) : (
        <HStack flex={1} minH={0} spacing={0} align="stretch">
          <Box
            {...FROZEN_RAIL_BG}
            w={`${COLUMN_WIDTHS.name}px`}
            minW={`${COLUMN_WIDTHS.name}px`}
            flexShrink={0}
            overflow="hidden"
            sx={{
              '& > div': { scrollbarWidth: 'none', msOverflowStyle: 'none' },
              '& > div::-webkit-scrollbar': { display: 'none' },
            }}
          >
            <List
              ref={leftListRef}
              height={listHeight}
              itemCount={rows.length}
              itemSize={getItemSize}
              itemData={frozenItemData}
              width={COLUMN_WIDTHS.name}
              overscanCount={5}
              onScroll={onLeftListScroll}
              style={{ overflowX: 'hidden', overflowY: 'auto' }}
            >
              {FrozenRow}
            </List>
          </Box>
          <Box
            ref={bodyScrollRef}
            flex={1}
            minW={0}
            overflowX="auto"
            overflowY="hidden"
            onScroll={(e) => {
              const left = e.currentTarget.scrollLeft;
              if (
                headerScrollRef.current &&
                headerScrollRef.current.scrollLeft !== left
              ) {
                headerScrollRef.current.scrollLeft = left;
              }
            }}
          >
            <Box minW={`${restTotalWidth}px`}>
              <List
                ref={rightListRef}
                height={listHeight}
                itemCount={rows.length}
                itemSize={getItemSize}
                itemData={scrollItemData}
                width={restTotalWidth}
                overscanCount={5}
                onScroll={onRightListScroll}
                style={{ overflowX: 'hidden', overflowY: 'auto' }}
              >
                {ScrollRow}
              </List>
            </Box>
          </Box>
        </HStack>
      )}
      </Box>
    </Box>
  );
};

export default ReviewerCampaignTable;
